import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, ChevronDown, Check, Plus, Pencil, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useReferenceStore, ReferenceOption, ReferenceStore } from '../store/useReferenceStore';
import ConfirmDialog from './ConfirmDialog';

// 1. We added defaultOptions here
interface ReferenceMultiSelectProps {
  label?: string;
  category: string;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  defaultOptions?: string[]; 
}

const EMPTY_ARRAY: ReferenceOption[] = [];

// Selections are stored as keys. If the option behind one is gone — deleted
// from the list, most likely — the chip would otherwise render the raw key
// ("languages_hindi"). Turn it back into something readable instead.
const keyAsLabel = (key: string, category?: string) => {
  const bare = category && key.startsWith(`${category}_`) ? key.slice(category.length + 1) : key;
  return bare.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const ReferenceMultiSelect: React.FC<ReferenceMultiSelectProps> = ({
  label,
  category,
  selectedValues,
  onChange,
  placeholder = "Select multiple...",
  defaultOptions
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  // Option being renamed inline (its key) + the working text.
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [renamingBusy, setRenamingBusy] = useState(false);
  // Option queued for deletion — held until the confirm dialog is answered.
  const [pendingDelete, setPendingDelete] = useState<ReferenceOption | null>(null);

  const addOption = useReferenceStore((state: ReferenceStore) => state.addOption);
  const renameOption = useReferenceStore((state: ReferenceStore) => state.renameOption);
  const deleteOption = useReferenceStore((state: ReferenceStore) => state.deleteOption);
  const containerRef = useRef<HTMLDivElement>(null);

  // 3. Zustand DB Options (Using the stable EMPTY_ARRAY to prevent infinite loops)
  const dbOptions = useReferenceStore((state: ReferenceStore) => state.options[category] || EMPTY_ARRAY);

  // 4. Merge DB Options with Default Options (Fixes the 'def' and 'category' errors)
  const options = useMemo(() => {
    const combined = [...dbOptions];
    if (defaultOptions) {
      defaultOptions.forEach((def: string) => {
        const key = def.toLowerCase().replace(/\s+/g, '_');
        if (!combined.some(o => o.key === key)) {
          combined.push({ key, label: def });
        }
      });
    }
    return combined;
  }, [JSON.stringify(dbOptions), JSON.stringify(defaultOptions)]);

  // Only options that actually exist in the database can be renamed or deleted;
  // `defaultOptions` are synthesised client-side and have no row behind them.
  const persistedKeys = useMemo(
    () => new Set(dbOptions.map((o: ReferenceOption) => o.key)),
    [dbOptions]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Leaving the dropdown abandons any half-finished inline rename.
  useEffect(() => {
    if (!isOpen) { setEditingKey(null); setEditingLabel(''); setHoveredKey(null); }
  }, [isOpen]);

  const toggleOption = (key: string) => {
    if (selectedValues.includes(key)) {
      onChange(selectedValues.filter(v => v !== key));
    } else {
      onChange([...selectedValues, key]);
    }
    setSearchTerm(''); 
  };

  const handleAddNew = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!searchTerm.trim() || isAdding) return;
    setIsAdding(true);

    try {
      await addOption(category, searchTerm.trim());
      const newKey = searchTerm.trim().toLowerCase().replace(/\s+/g, '_');
      
      if (!selectedValues.includes(newKey)) {
        onChange([...selectedValues, newKey]);
      }
      setSearchTerm('');
    } catch (error) {
      console.error("Failed to add new option", error);
    } finally {
      setIsAdding(false);
    }
  };

  const beginRename = (opt: ReferenceOption) => {
    setEditingKey(opt.key);
    setEditingLabel(opt.label);
  };

  const cancelRename = () => {
    setEditingKey(null);
    setEditingLabel('');
  };

  const commitRename = async (opt: ReferenceOption) => {
    const next = editingLabel.trim();
    if (!next || next === opt.label) { cancelRename(); return; }
    setRenamingBusy(true);
    try {
      // Selections here are stored by key, so they follow a rename on their own.
      await renameOption(category, opt.key, next);
      toast.success('Renamed');
      cancelRename();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to rename');
    } finally {
      setRenamingBusy(false);
    }
  };

  const confirmDelete = async () => {
    const opt = pendingDelete;
    setPendingDelete(null);
    if (!opt) return;
    try {
      await deleteOption(category, opt.key);
      // A deleted option can't stay picked — it would render as a raw key.
      if (selectedValues.includes(opt.key)) {
        onChange(selectedValues.filter(v => v !== opt.key));
      }
      toast.success('Removed from list');
    } catch {
      toast.error('Failed to remove');
    }
  };

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((o: ReferenceOption) => 
      o.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, options]);

  const exactMatchExists = options.some(
    (o: ReferenceOption) => o.label.toLowerCase().trim() === searchTerm.trim().toLowerCase()
  );

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative', width: '100%' }}>
      {label && (
        <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.025em' }}>
          {label}
        </label>
      )}
      
      <div 
        onClick={() => setIsOpen(true)}
        style={{ 
          padding: '0 8px', 
          border: `1px solid ${isOpen ? '#3b82f6' : '#cbd5e1'}`, 
          borderRadius: '6px', 
          backgroundColor: '#fff', 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '6px', 
          cursor: 'text', 
          minHeight: '40px', 
          alignItems: 'center',
          transition: 'border-color 0.2s',
          boxSizing: 'border-box'
        }}
      >
        {selectedValues.map(val => {
          const opt = options.find((o: ReferenceOption) => o.key === val);
          return (
            <span key={val} style={{ 
              backgroundColor: '#eff6ff', 
              color: '#1d4ed8', 
              padding: '2px 8px', 
              borderRadius: '4px', 
              fontSize: '13px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px', 
              border: '1px solid #bfdbfe', 
              margin: '4px 0' 
            }}>
              {opt ? opt.label : keyAsLabel(val, category)}
              <X 
                size={14} 
                onClick={(e) => { e.stopPropagation(); toggleOption(val); }} 
                style={{ cursor: 'pointer' }} 
              />
            </span>
          );
        })}
        
        <input 
          type="text"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
          style={{ 
            border: 'none', 
            outline: 'none', 
            flex: 1, 
            minWidth: '60px', 
            fontSize: '14px', 
            backgroundColor: 'transparent',
            height: '28px', 
            margin: 0,
            padding: 0,
            color: '#1e293b'
          }}
          placeholder={selectedValues.length === 0 ? placeholder : ''}
        />

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', color: '#94a3b8' }}>
           <ChevronDown size={16} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }} />
        </div>
      </div>

      {isOpen && (
        <div style={{ 
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1000, 
          backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', 
          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)', 
          maxHeight: '250px', overflowY: 'auto'
        }}>
          {filteredOptions.map((opt: ReferenceOption) => {
            const isSelected = selectedValues.includes(opt.key);
            const isEditing = editingKey === opt.key;
            // Actions stay hidden until the row is hovered, so the list reads as
            // a list of choices rather than a row of buttons.
            const showActions = persistedKeys.has(opt.key) && (hoveredKey === opt.key || isEditing);
            return (
              <div
                key={opt.key}
                onMouseEnter={() => setHoveredKey(opt.key)}
                onMouseLeave={() => setHoveredKey(prev => (prev === opt.key ? null : prev))}
                style={{
                  padding: '10px 10px 10px 12px', fontSize: '14px',
                  backgroundColor: isEditing ? '#fffbeb' : isSelected ? '#eff6ff' : (hoveredKey === opt.key ? '#f8fafc' : 'transparent'),
                  color: isSelected ? '#1d4ed8' : '#1e293b',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  borderBottom: '1px solid #f1f5f9'
                }}
              >
                {isEditing ? (
                  <>
                    {/* Inline rename — fix a typo without leaving the dropdown. */}
                    <input
                      autoFocus
                      value={editingLabel}
                      onChange={(e) => setEditingLabel(e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); commitRename(opt); }
                        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelRename(); }
                      }}
                      style={{ flex: 1, minWidth: 0, padding: '3px 6px', border: '1px solid #93c5fd', borderRadius: '4px', fontSize: '14px', outline: 'none' }}
                    />
                    <Check
                      size={15}
                      style={{ cursor: renamingBusy ? 'wait' : 'pointer', color: '#16a34a', flexShrink: 0 }}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); commitRename(opt); }}
                    />
                    <X
                      size={15}
                      style={{ cursor: 'pointer', color: '#94a3b8', flexShrink: 0 }}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); cancelRename(); }}
                    />
                  </>
                ) : (
                  <>
                    <span
                      onMouseDown={(e) => { e.preventDefault(); toggleOption(opt.key); }}
                      style={{ flex: 1, minWidth: 0, cursor: 'pointer', fontWeight: isSelected ? 600 : 400, overflowWrap: 'anywhere' }}
                    >
                      {opt.label}
                    </span>
                    {isSelected && <Check size={16} style={{ color: '#3b82f6', flexShrink: 0 }} />}
                    <span
                      title="Rename"
                      style={{ display: 'inline-flex', flexShrink: 0, visibility: showActions ? 'visible' : 'hidden' }}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); beginRename(opt); }}
                    >
                      <Pencil size={14} style={{ cursor: 'pointer', color: '#64748b' }} />
                    </span>
                    <span
                      title="Delete"
                      style={{ display: 'inline-flex', flexShrink: 0, visibility: showActions ? 'visible' : 'hidden' }}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setPendingDelete(opt); }}
                    >
                      <Trash2 size={14} style={{ cursor: 'pointer', color: '#ef4444' }} />
                    </span>
                  </>
                )}
              </div>
            );
          })}
          
          {searchTerm.trim() !== '' && !exactMatchExists && (
            <div 
              onMouseDown={handleAddNew}
              style={{ 
                padding: '12px', 
                color: '#2563eb', 
                backgroundColor: '#eff6ff',
                fontWeight: 600, 
                fontSize: '13px', 
                cursor: isAdding ? 'wait' : 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px'
              }}
            >
              <Plus size={16} /> {isAdding ? 'Saving...' : `Add "${searchTerm.trim()}" permanently`}
            </div>
          )}

          {filteredOptions.length === 0 && searchTerm === '' && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
              No options available. Type to add.
            </div>
          )}
        </div>
      )}

      {/* Deleting takes the entry off the list for everyone, so name the
          consequence before the click rather than after. */}
      <ConfirmDialog
        open={!!pendingDelete}
        danger
        title={`Delete "${pendingDelete?.label || ''}" from the ${(label || category).toLowerCase()} list?`}
        message="It will no longer be offered in this dropdown."
        points={[
          'It will be unselected here, and on records saved earlier it shows as plain text.',
          'It can be added back later by typing it in again.',
        ]}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default ReferenceMultiSelect;